import { withTranslation } from 'react-i18next';
import numeral from 'numeral';

import DataTable from '../../../../components/Datatable';
import { getSortKeys, setAdditionalStyles } from '../../../../helpers/table';
import { searchTypeLabel } from '../../GscTools/period';
import { SCOPE_COLUMNS } from '../keys';
import { segmentLabel } from '../segments';

/**
 * Block 04 — what the report was read under, and the export of exactly that.
 *
 * The export is one partition per file: the active dimension's rows and nothing else. Country
 * rows and appearance rows both cut this property's traffic, but they cut it differently and
 * overlap, so a file holding both would have a clicks column that adds up to more than the
 * property received. Block 03's contributors export themselves, from their own table, for the
 * same reason.
 *
 * The page rule is shown as the expression Search Console was actually sent rather than as the
 * prefix that was typed, because that is what bounded the figures — and because a prefix anchored
 * after the origin is not what a reader would have guessed from the text in the box.
 */
function SegmentCoverage({ t, scope, status, returnedRows, exportable, onExport }) {
    const columns = SCOPE_COLUMNS(t);
    const notApplied = t('tools.audience_appearance.coverage.not_applied');
    const notSent = t('tools.audience_appearance.export.not_sent');

    const rows = [
        {
            id: 'source',
            item: t('tools.audience_appearance.coverage.source'),
            value: searchTypeLabel(scope.searchType, t),
        },
        {
            id: 'dimension',
            item: t('tools.audience_appearance.coverage.dimension'),
            value: `${segmentLabel(t, scope.segment)} (${scope.segment})`,
        },
        {
            id: 'country',
            item: t('tools.gsc.controls.country'),
            value: scope.takesFilters
                ? scope.country?.toUpperCase() || t('tools.gsc.controls.all_countries')
                : notSent,
        },
        {
            id: 'device',
            item: t('tools.gsc.controls.device'),
            value: scope.takesFilters
                ? (scope.device && t(`tools.gsc.devices.${scope.device.toLowerCase()}`)) ||
                  t('tools.gsc.controls.all_devices')
                : notSent,
        },
        {
            id: 'page_rule',
            item: t('tools.audience_appearance.coverage.page_rule'),
            value: scope.takesFilters ? scope.pageRegex ?? notApplied : notSent,
        },
        {
            id: 'returned',
            item: t('tools.audience_appearance.coverage.returned'),
            value:
                status === 'done'
                    ? numeral(returnedRows).format('0,0')
                    : t('tools.gsc.not_returned'),
        },
        {
            id: 'coverage',
            item: t('tools.audience_appearance.coverage.coverage'),
            value: t('tools.audience_appearance.coverage.coverage_value'),
        },
    ];

    return (
        <div className="card table-main rounded-main overflow-hidden gsc-tool-block">
            <div className="card-header d-flex justify-content-between">
                <div>
                    <p className="card-title">{t('tools.audience_appearance.coverage.title')}</p>
                    <p className="gsc-tool-sub">
                        {t('tools.audience_appearance.coverage.description')}
                    </p>
                </div>

                <div className="gsc-tool-icon-actions">
                    <button
                        type="button"
                        title={t('tools.audience_appearance.coverage.export')}
                        aria-label={t('tools.audience_appearance.coverage.export')}
                        onClick={onExport}
                        disabled={!exportable}
                    >
                        <i className="icon-fi_download" />
                    </button>
                </div>
            </div>

            <div className="card-body">
                <DataTable
                    nodes={rows}
                    COLUMNS={columns}
                    sortFns={getSortKeys(columns)}
                    additionalStyles={setAdditionalStyles(columns)}
                    onlyTable
                    notUseDefaultStyle
                    customContainerStyles="gsc-tool-rules-table"
                />

                <p className="gsc-tool-table-note">
                    {t('tools.audience_appearance.coverage.export_note')}
                </p>
                <p className="gsc-tool-table-note">
                    {t('tools.audience_appearance.coverage.partition_note')}
                </p>
            </div>
        </div>
    );
}

export default withTranslation()(SegmentCoverage);
